import { ReactNode, useEffect, useState } from 'react';
import { Tab } from '@headlessui/react';
import moment from 'moment';
import { useGetTipsFeedbackDetailsQuery } from '../../../../store/chatbox/chatboxApi';
import TipsFeedbackWorkflow from './TipsFeedbackWorkflow';
import TipsFeedbackAnalysis from './TipsFeedbackAnalysis';
import FeedbackDrawer from './FeedbackDrawer';
import FeedbackStatus from './FeedbackStatus';
import { feedbackErrorMessage, feedbackStatusLabel } from '../utils';
import {
    ITipsFeedbackItem,
    TLeoFeedbackTab,
    TFeedbackDetailsSection,
} from '../../../../interfaces/tips-feedback/ITipsFeedback';

const formatDate = (value?: string, format = 'DD MMM YYYY, HH:mm') => {
    if (!value || !moment(value).isValid()) return '--';
    return moment(value).format(format);
};

const textValue = (value?: string) => value?.trim() ? value : '--';

const getClientName = (item?: ITipsFeedbackItem) =>
    item?.client || item?.userCreated?.companyData?.username;

const DetailRow = ({ label, children }: { label: string; children?: ReactNode }) => (
    <div className='grid min-w-0 border-b border-gray-200 last:border-b-0 dark:border-gray-700 sm:grid-cols-[170px_minmax(0,1fr)]'>
        <dt className='px-3 pt-3 text-sm text-gray-500 dark:text-gray-400 sm:pb-3'>{label}</dt>
        <dd className='min-w-0 whitespace-pre-wrap break-words px-3 pb-3 pt-1 text-sm text-gray-900 dark:text-gray-100 sm:pt-3'>{children || '--'}</dd>
    </div>
);

const MetaItem = ({ label, value }: { label: string; value?: string }) => <DetailRow label={label}>{textValue(value)}</DetailRow>;

const SectionBlock = ({ title, value, tone = 'neutral' }: {
    title: string;
    value?: string;
    tone?: 'neutral' | 'green' | 'orange';
}) => {
    const toneStyles = {
        neutral: 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black',
        green: 'border-[#D3E8DD] bg-[#EAF5F0] dark:border-[#2D5E47] dark:bg-[#143526]',
        orange: 'border-[#F0E2D6] bg-[#F8EFE7] dark:border-[#5D4637] dark:bg-[#3B2B21]',
    };
    return (
        <section className={`min-w-0 rounded-2xl border p-4 ${toneStyles[tone]}`}>
            <h4 className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300'>{title}</h4>
            <p className='whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-900 dark:text-white'>{textValue(value)}</p>
        </section>
    );
};

const NavigationIcon = ({ index }: { index: number }) => (
    <svg aria-hidden='true' width='19' height='19' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' className='shrink-0'>
        {index === 0 && <><rect x='5' y='3' width='14' height='18' rx='2' /><path d='M9 8h6M9 12h6M9 16h4' /></>}
        {index === 1 && <><circle cx='10.5' cy='10.5' r='6.5' /><path d='m16 16 5 5M8 10.5h5M10.5 8v5' /></>}
        {index === 3 && <><path d='m12 3 2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4L12 3Z' /></>}
        {index === 2 && <><path d='M4 7h9m4 0h3M4 17h3m4 0h9' /><circle cx='15' cy='7' r='2' /><circle cx='9' cy='17' r='2' /></>}
    </svg>
);

const panelStyle = 'h-full min-w-0 overflow-y-auto overscroll-contain p-4 outline-none sm:p-5 lg:p-6 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green-700';

const TipsFeedbackDetailsModal = ({
    activeTab = 'tips',
    initialSection = 'details',
    open,
    onClose,
    item: selectedItem,
    onSaved,
}: {
    activeTab?: TLeoFeedbackTab;
    initialSection?: TFeedbackDetailsSection;
    open: boolean;
    onClose: () => void;
    item?: ITipsFeedbackItem;
    onSaved: () => void;
}) => {
    const [verticalTabs, setVerticalTabs] = useState(false);
    useEffect(() => {
        const media = window.matchMedia?.('(min-width: 768px)');
        if (!media) return;
        const updateOrientation = () => setVerticalTabs(media.matches);
        updateOrientation();
        media.addEventListener('change', updateOrientation);
        return () => media.removeEventListener('change', updateOrientation);
    }, []);

    const { currentData, isFetching, error, refetch } = useGetTipsFeedbackDetailsQuery(selectedItem?._id || '', {
        skip: !open || !selectedItem?._id,
        refetchOnMountOrArgChange: true,
    });
    const details = currentData?._id === selectedItem?._id ? currentData : undefined;
    const item = details || selectedItem;
    if (!item) return null;

    const isLinksTab = activeTab === 'links';
    const isChallengeTab = activeTab === 'challenge';
    const tabs = [isLinksTab ? 'Link details' : isChallengeTab ? 'Challenge details' : 'Tip details', 'Leo investigation', 'Manage feedback', 'Analysis'];
    const sections: TFeedbackDetailsSection[] = ['details', 'investigation', 'manage', 'analysis'];
    const unavailableMessage = isFetching ? 'Loading feedback details...' : 'Feedback details could not be loaded. Use Retry above to try again.';

    return (
        <FeedbackDrawer open={open} onClose={onClose} title='View Feedback'>
            {isFetching && <p role='status' className='shrink-0 border-b border-gray-200 px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400 sm:px-6'>Loading feedback details...</p>}
            {(error || (!isFetching && !details)) && (
                <div role='alert' className='shrink-0 border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300 sm:px-6'>
                    {feedbackErrorMessage(error, 'Unable to load feedback details. Please try again.')}
                    <button type='button' onClick={() => refetch()} className='ml-2 underline' disabled={isFetching}>Retry</button>
                </div>
            )}

            <Tab.Group key={`${item._id}:${initialSection}`} defaultIndex={sections.indexOf(initialSection)} as='div' vertical={verticalTabs} className='flex min-h-0 flex-1 flex-col md:flex-row'>
                <Tab.List aria-label='Feedback sections' className='grid shrink-0 grid-cols-2 sm:grid-cols-4 gap-1 border-b border-gray-200 bg-gray-50/70 p-2 dark:border-gray-700 dark:bg-black/20 md:flex md:w-56 md:flex-col md:gap-2 md:border-b-0 md:border-r md:p-3'>
                    {tabs.map((label, index) => (
                        <Tab key={label} className={({ selected }) => `flex min-w-0 items-center justify-center gap-1.5 rounded-xl px-2 py-3 text-center text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-green-700 sm:gap-2 md:justify-start md:px-3 md:text-left md:text-sm ${selected ? 'bg-[#00330f] text-white dark:bg-white dark:text-black' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10'}`}>
                            <span className='hidden sm:block'><NavigationIcon index={index} /></span>
                            <span>{label}</span>
                        </Tab>
                    ))}
                </Tab.List>

                <Tab.Panels className='min-h-0 min-w-0 flex-1'>
                    <Tab.Panel unmount={false} className={panelStyle}>
                        <h3 className='text-base font-medium text-gray-800 dark:text-gray-100'>Leo Feedback {isLinksTab ? 'Link' : isChallengeTab ? 'Challenge' : 'Tip'} Details</h3>
                        <dl className='mt-3 border border-gray-200 dark:border-gray-700'>
                            <MetaItem label='Date' value={formatDate(item.date || item.dateOfTip, 'YYYY-MM-DD')} />
                            <MetaItem label='Client' value={getClientName(item)} />
                            <MetaItem label='Property' value={item.property} />
                            <DetailRow label='Status'><FeedbackStatus status={item.workflowStatus} label={item.status} /></DetailRow>
                            <DetailRow label='Reporter Details'><div className='flex flex-wrap justify-between gap-x-4 gap-y-1'><span>{item.reporterName || '--'}</span><span className='break-all'>{item.reporterEmail || '--'}</span></div></DetailRow>
                        </dl>
                        <h3 className='mt-7 text-base font-medium text-gray-800 dark:text-gray-100'>Feedback</h3>
                        <dl className='mt-3 border border-gray-200 dark:border-gray-700'>
                            {isLinksTab ? <>
                                <MetaItem label='Link' value={item.link || item.billyUrl} />
                                <MetaItem label='Source' value={item.actionArea} />
                                <MetaItem label='Link type' value={item.recommendedAction} />
                                <MetaItem label='Selected reason' value={item.reasoning} />
                            </> : <>
                                <MetaItem label='Action Area' value={item.actionArea} />
                                <MetaItem label='Recommended Action' value={item.recommendedAction} />
                                <MetaItem label='Reasoning' value={item.reasoning} />
                            </>}
                            <MetaItem label='Feedback' value={item.feedback} />
                            {details?.prompt && <MetaItem label='Affected prompt' value={details.prompt} />}
                        </dl>
                        <details className='mt-5 text-sm text-gray-500 dark:text-gray-400'>
                            <summary className='cursor-pointer py-2'>Record information</summary>
                            <dl className='mt-2 border border-gray-200 dark:border-gray-700'>
                                <MetaItem label='Feedback ID' value={item._id} />
                                {!!item.tipId && <MetaItem label='Tip ID' value={item.tipId} />}
                                {details?.promptId && <MetaItem label='Prompt ID' value={details.promptId} />}
                                {details?.submittedByUsername && <MetaItem label='Submitted by' value={details.submittedByUsername} />}
                            </dl>
                        </details>
                    </Tab.Panel>

                    <Tab.Panel unmount={false} className={panelStyle}>
                        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>Leo investigation</h3>
                        <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>Investigation findings and the history of this feedback.</p>
                        {details ? (
                            <>
                                <div className='mt-4 space-y-3'>
                                    <SectionBlock title='Investigation' value={details.investigation || 'No investigation recorded yet.'} />
                                    {details.reason && <SectionBlock title='Cannot resolve reason' value={details.reason} tone='orange' />}
                                </div>
                                <section className='mt-6'>
                                    <h4 className='mb-3 text-sm font-semibold dark:text-white'>Workflow history</h4>
                                    {details.timeline?.length ? (
                                        <ol className='space-y-4'>
                                            {details.timeline.map((entry, index) => (
                                                <li key={index} className='min-w-0 border-l-2 border-gray-200 pl-4 text-sm dark:border-gray-600 dark:text-gray-300'>
                                                    <p className='font-medium'>{feedbackStatusLabel(entry.status)}</p>
                                                    <p className='mt-1 break-words text-xs text-gray-500 dark:text-gray-400'>{formatDate(entry.at)}{entry.actor ? ` • ${entry.actor}` : ''}</p>
                                                    {entry.note && <p className='mt-2 whitespace-pre-wrap break-words'>{entry.note}</p>}
                                                </li>
                                            ))}
                                        </ol>
                                    ) : <p className='text-sm text-gray-500 dark:text-gray-400'>No workflow history recorded yet.</p>}
                                </section>
                            </>
                        ) : <p className='mt-4 text-sm text-gray-500 dark:text-gray-400'>{unavailableMessage}</p>}
                    </Tab.Panel>

                    <Tab.Panel unmount={false} className={panelStyle}>
                        {details ? (
                            <>
                                <TipsFeedbackWorkflow key={details._id} item={details} disabled={isFetching || !!error} onSaved={onSaved} />
                                {(details.replacementTipId || details.clientNotificationStatus || details.clientNotifiedAt || details.productTeamNotifiedAt) && (
                                    <section className='mt-6 border-t border-gray-200 pt-5 dark:border-gray-700'>
                                        <h4 className='mb-3 text-sm font-semibold dark:text-white'>Resolution & notifications</h4>
                                        <dl className='border border-gray-200 dark:border-gray-700'>
                                            {details.replacementTipId && <MetaItem label='Replacement tip ID' value={details.replacementTipId} />}
                                            {details.clientNotificationStatus && <MetaItem label='Client notification status' value={details.clientNotificationStatus.replace(/_/g, ' ')} />}
                                            {details.clientNotifiedAt && <MetaItem label='Client notified at' value={formatDate(details.clientNotifiedAt)} />}
                                            {details.productTeamNotifiedAt && <MetaItem label='Product team notified at' value={formatDate(details.productTeamNotifiedAt)} />}
                                        </dl>
                                    </section>
                                )}
                            </>
                        ) : <p className='text-sm text-gray-500 dark:text-gray-400'>{unavailableMessage}</p>}
                    </Tab.Panel>
                    <Tab.Panel unmount={false} className='h-full min-h-0 min-w-0 outline-none'>
                        <TipsFeedbackAnalysis key={item._id} feedbackId={item._id} filters={{ type: isLinksTab ? 'link_audit' : isChallengeTab ? 'challenge' : 'tips' }} />
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
        </FeedbackDrawer>
    );
};

export default TipsFeedbackDetailsModal;
